import { profile_image_upload_path, scientist_profile_path } from "@routes/routes";
import { marketplacePath } from "@routes/utils";
import { SegmentEventName } from "@tsTypes/__generated__/enums";
import { ScientistCurrentUser, UsersShowResponse } from "@tsTypes/index";
import { User } from "@tsTypes/schema";
import { UserRole } from "@tsTypes/users";
import { track } from "@utils/appUtils";
import devConsole from "@utils/devConsole";
import axios, { AxiosError } from "axios";
import { UserDetailedType } from "src/views/Onboarding/OnboardingContainer";
import { CompanyScientistOnboardingProfileInfo } from "src/views/Onboarding/Step3/CompanyScientist";
import { CompanySponsorOnboardingProfileInfo } from "src/views/Onboarding/Step3/CompanySponsor";
import { UniversityAdminOnboardingProfileInfo } from "src/views/Onboarding/Step3/ResearchInstitutionAdmin";
import { UniversityScientistOnboardingProfileInfo } from "src/views/Onboarding/Step3/ResearchInstitutionScientist";
import { HOW_DID_YOU_HEAR_ABOUT_US_OTHER_OPTION } from "src/views/Onboarding/Step3/Step3";
import request from "./request";

export class UserNotVerifiedError extends Error {}

export const getUser = async (
  userId: number,
  fetchFromSession = false
): Promise<UsersShowResponse | null> => {
  try {
    const response = fetchFromSession
      ? await request.get("/me", {
          // The timestamp param is added to force a new request as opposed to using a cached response
          params: { timestamp: Number(new Date()) },
        })
      : await request.get(`/users/${userId}`);

    return response.data;
  } catch (error) {
    switch (error.response?.status) {
      case 401:
      case 404:
        break;
      default:
        if (window.location.pathname !== "/") {
          devConsole.error("Redirecting to / since after failure.");
          window.location.href = "/";
        }
    }
    return null;
  }
};

export const signOut = async () => {
  await request.delete("/sign_out");
  // This needs to use window.location.href instead of history.push to prevent
  // accessing the previous session via the browser cache
  window.location.href = marketplacePath();
};

export const signIn = async ({ email, password }) =>
  request.post("/users/sign_in", {
    user: {
      email,
      password,
    },
  });

export const register = async ({
  email,
  password,
  firstName,
  lastName,
}: {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}) =>
  request.post("/users", {
    user: {
      email,
      password,
      first_name: firstName,
      last_name: lastName,
    },
  });

export const cancelSignUp = async (source) => {
  track(SegmentEventName.ABANDON, {
    react_component: "OnboardingContainer",
    source,
  });
  return request.delete("/onboarding");
};

export const onboard = ({
  userType,
  profileInfo,
  detailedType = null,
  commenterInviteRequest = null,
}: {
  userType: "scientist" | "sponsor" | "admin";
  profileInfo:
    | CompanyScientistOnboardingProfileInfo
    | CompanySponsorOnboardingProfileInfo
    | UniversityScientistOnboardingProfileInfo
    | UniversityAdminOnboardingProfileInfo;
  detailedType?: UserDetailedType | null;
  commenterInviteRequest?: string | null;
}) => {
  let data: Record<string, unknown> = {
    how_did_you_hear_about_us:
      profileInfo.howDidYouHearAboutUs === HOW_DID_YOU_HEAR_ABOUT_US_OTHER_OPTION
        ? profileInfo.howDidYouHearAboutUsOther
        : profileInfo.howDidYouHearAboutUs,
    detailed_type: detailedType,
  };

  switch (userType) {
    case "scientist": {
      const typedProfileInfo = profileInfo as
        | CompanyScientistOnboardingProfileInfo
        | UniversityScientistOnboardingProfileInfo;

      data = {
        ...data,
        account_type: UserRole.SCIENTIST,
        profile_info: {
          title: typedProfileInfo.role,
          disciplines: typedProfileInfo.disciplines,
          areas_of_expertise: typedProfileInfo.areasOfExpertise,
          ...(typedProfileInfo.university && {
            university: typedProfileInfo.university,
          }),
        },
        ...("companyName" in typedProfileInfo && {
          startup: {
            selected_company_id: Number(typedProfileInfo.selectedCompanyId),
            startup_name: typedProfileInfo.companyName,
            url: typedProfileInfo.companyURL && encodeURI(typedProfileInfo.companyURL),
          },
        }),
        ...(typedProfileInfo.country_code && {
          country_code: typedProfileInfo.country_code,
        }),
      };
      break;
    }
    case "sponsor": {
      const typedProfileInfo = profileInfo as CompanySponsorOnboardingProfileInfo;

      data = {
        ...data,
        account_type: UserRole.SPONSOR,
        profile_info: {
          role: typedProfileInfo.role,
          title: typedProfileInfo.title,
          areas_of_expertise: typedProfileInfo.areasOfExpertise,
        },
        company: typedProfileInfo.company,
        ...(commenterInviteRequest && { commenter_invite_request: commenterInviteRequest }),
      };
      break;
    }
    case "admin": {
      const typedProfileInfo = profileInfo as UniversityAdminOnboardingProfileInfo;

      data = {
        ...data,
        account_type: UserRole.UNIVERSITY_ADMIN,
        profile_info: {
          university: typedProfileInfo.university,
          disciplines: typedProfileInfo.disciplines,
        },
        ...(typedProfileInfo.country_code && {
          country_code: typedProfileInfo.country_code,
        }),
      };
      break;
    }
  }

  return request
    .post(`/onboarding`, data)
    .catch((error: Error | AxiosError) => {
      if (axios.isAxiosError(error) && error?.response?.status === 403) {
        throw new UserNotVerifiedError(error?.response?.data?.message);
      } else {
        throw error;
      }
    })
    .then((response) => ({ ...response.data.user, viewed_tours: [] }));
};

export const updateUserKeywords = (
  userId: string,
  keywords: { disciplines: string[]; areasOfExpertise: string[] }
) => {
  return request.put(`/users/${userId}`, {
    disciplines: keywords.disciplines,
    areas_of_expertise: keywords.areasOfExpertise,
  });
};

export const updateUserProfile = async (
  userId: number,
  role: UserRole,
  profileInfo: any
): Promise<ScientistCurrentUser> => {
  let url = "";

  switch (role) {
    case UserRole.SCIENTIST:
      url = scientist_profile_path(userId);
      break;
    case UserRole.SPONSOR:
      url = `/sponsor_profiles/${profileInfo.id}`;
      break;
    case UserRole.UNIVERSITY_ADMIN:
      url = `/university_profiles/${profileInfo.id}`;
      break;
  }

  const response = await request.put(url, {
    ...profileInfo,
    user_id: userId,
  });

  return response.data.user;
};

export const skipUpdateUserKeywords = (userId: string) => {
  return request.put(`/users/${userId}`, {
    has_updated_taxonomy: true,
  });
};

export const resendEmailInvite = async (email: string): Promise<void> => {
  await request.post("/register/resend_email_verification", { email });
};

export const verifyUser = async (verificationCode: string): Promise<boolean> => {
  return (await request.post("/onboarding/verify", { verification_code: verificationCode })).data
    .success;
};

export const uploadProfileImage = async (file: File): Promise<Pick<User, "image">> => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await request.post(profile_image_upload_path(), formData);
  return response.data;
};
